import React, { useState } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, Space, Typography, Tag, message } from 'antd';
import { 
  UserOutlined, 
  LogoutOutlined, 
  SettingOutlined, 
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  CrownOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAppNavMenu } from '../../hooks/useAppNavMenu';
import HeaderCreditBalance from './HeaderCreditBalance';
import AppNavDrawer from './AppNavDrawer';
import AppBottomNav from './AppBottomNav';
import { USER_TYPE_LABELS, USER_TYPE_COLORS } from '../../config/supertokens';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { balance: creditBalance } = useCreditBalance();
  const isMobile = useIsMobile();
  const { menuItems, openKeys, setOpenKeys } = useAppNavMenu();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const brandIdFromPath = location.pathname.match(/^\/brands\/([^/]+)/)?.[1] ?? null;

  const navigateToBrandProfile = () => {
    if (brandIdFromPath) {
      navigate(`/brands/${brandIdFromPath}`);
      return;
    }
    navigate('/dashboard');
  };

  const navigateToBrandSettings = () => {
    if (brandIdFromPath) {
      navigate(`/brands/${brandIdFromPath}/settings`);
      return;
    }
    message.warning('Select a brand from the dashboard to open settings.');
    navigate('/dashboard');
  };

  const userMenuItems = [
    {
      key: 'user-info',
      label: (
        <div className="px-1 py-2">
          <div className="font-medium text-gray-900">{user?.name}</div>
          <div className="text-xs text-gray-500 mt-0.5">{user?.email}</div>
          <Tag 
            color={USER_TYPE_COLORS[user?.user_type as keyof typeof USER_TYPE_COLORS] || 'default'}
            className="mt-2 text-xs"
          >
            {USER_TYPE_LABELS[user?.user_type as keyof typeof USER_TYPE_LABELS] || user?.user_type}
          </Tag>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'profile',
      icon: <UserOutlined className="text-gray-500" />,
      label: <span className="text-gray-700">Profile</span>,
      onClick: navigateToBrandProfile,
    },
    {
      key: 'settings',
      icon: <SettingOutlined className="text-gray-500" />,
      label: <span className="text-gray-700">Settings</span>,
      onClick: navigateToBrandSettings,
    },
    ...(isSuperAdmin ? [{
      key: 'admin',
      icon: <CrownOutlined className="text-amber-500" />,
      label: <span className="text-amber-600 font-medium">Admin Panel</span>,
      onClick: () => navigate('/admin'),
    }] : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined className="text-gray-500" />,
      label: <span className="text-gray-700">Logout</span>,
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout style={{ height: '100dvh', overflow: 'hidden' }}>
      {/* Desktop fixed sidebar — hidden on mobile */}
      {!isMobile && (
        <Sider 
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          className="bg-white border-r border-gray-200 fixed left-0 top-0 h-screen overflow-auto"
          width={260}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 100
          }}
        >
          {/* Logo area */}
          <div className="h-16 px-4 border-b border-gray-200 flex items-center">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isSuperAdmin 
                  ? 'bg-amber-500' 
                  : 'bg-primary-600'
              }`}>
                <Text className="text-white font-semibold text-sm">{isSuperAdmin ? 'A' : 'B'}</Text>
              </div>
              {!collapsed && (
                <div>
                  <Text className="font-semibold text-gray-900 text-sm block leading-tight">
                    {isSuperAdmin ? 'Admin Panel' : 'BRAI'}
                  </Text>
                  <Text className="text-xs text-gray-500">
                    {isSuperAdmin ? 'System Management' : 'Brand Intelligence'}
                  </Text>
                </div>
              )}
            </div>
          </div>
          
          {/* Navigation menu */}
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys as string[])}
            items={menuItems}
            className="border-none py-2"
            style={{ background: 'transparent' }}
          />
        </Sider>
      )}

      {/* Mobile nav drawer */}
      {isMobile && (
        <AppNavDrawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
        />
      )}
      
      <AntLayout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 260),
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'margin-left 0.2s ease',
        }}
      >
        {/* Clean header */}
        <Header
          className="bg-white border-b border-gray-200 flex justify-between items-center flex-shrink-0 h-16 sticky top-0 z-40"
          style={{ padding: isMobile ? '0 16px' : '0 24px' }}
        >
          <div className="flex items-center">
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileDrawerOpen(true)}
                className="mr-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="mr-4 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              />
            )}
            <Text className="text-gray-700 font-medium hidden sm:block">
              {isSuperAdmin ? 'Admin Control Panel' : `Welcome, ${user?.name}`}
            </Text>
            {isSuperAdmin && !isMobile && (
              <Tag className="ml-3 bg-amber-50 text-amber-700 border-amber-200">
                <CrownOutlined className="mr-1" />
                Super Admin
              </Tag>
            )}
          </div>
          
          <div className="flex items-center">
            <Space size={isMobile ? 8 : 16}>
              {creditBalance !== null && !isMobile && <HeaderCreditBalance balance={creditBalance} />}
              {creditBalance !== null && isMobile && (
                <Tag
                  color={creditBalance > 0 ? 'blue' : 'red'}
                  className="cursor-pointer m-0 tap-scale text-xs"
                  style={{ padding: '2px 8px' }}
                  onClick={() => navigate('/credits')}
                >
                  <WalletOutlined /> {creditBalance.toLocaleString()}
                </Tag>
              )}
              <div className="text-right hidden sm:block">
                <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                <div className="text-xs text-gray-500">{user?.company}</div>
              </div>
              {!isMobile && (
                <Dropdown
                  menu={{ items: userMenuItems }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Avatar 
                    icon={<UserOutlined />} 
                    className="cursor-pointer bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    size={36}
                  />
                </Dropdown>
              )}
            </Space>
          </div>
        </Header>
        
        {/* Inner scroll region */}
        <Content
          className="bg-gray-50"
          style={{
            flex: '1 1 0',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            padding: isMobile ? '12px' : '24px',
            paddingBottom: isMobile
              ? 'calc(var(--bottom-nav-total, 80px) + 12px)'
              : '24px',
          }}
        >
          <div
            style={{
              flex: '1 1 0',
              minHeight: 0,
              width: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </div>
        </Content>
      </AntLayout>

      {/* Mobile bottom navigation */}
      <AppBottomNav />
    </AntLayout>
  );
};

export default AppLayout;
